import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  inject,
} from "@angular/core";
import { LoggerService } from "../../services/logger.service";
import { BehaviorSubject, Subject } from "rxjs";
import { Clipboard } from "@angular/cdk/clipboard";
import { CommonModule } from "@angular/common";
import {
  ChangeEvent,
  ExclusionTypeConfig,
  FilterItem,
  Resource,
  TableBackgroundColors,
  TableCell,
  TableColumn,
  TableRow,
  TableState,
  TotalRowsPayload,
} from "../../models/common-table";
import { PaginationState } from "../../models/pagination";
import { Currency } from "../../models/currency";
import { DEFAULT_PAGE_SIZE } from "../../constant/tables";
import { LookerStudioService } from "../../services/looker-studio.service";
import { defaultSerializer, DownloadType, SortOrder } from "../../constant/common-table";
import { EMPTY_VALS } from "../../constant/validation";
import { GroupByPayload } from "../../models/vcloud-api";
import { environment } from "../../../../environments/environment";
import {
  getActionButtonConfig,
  getColumnMomTrend,
  getColumnTotal,
  getTooltipUrl,
  openTooltipUrl,
  toStateClass,
} from "../../utils/common-table.utils";
import { CommonTableFacadeService, EditModeState } from "../../services/common-table/common-table-facade.service";
import { CommonTableDownloadService } from "../../services/common-table/common-table-download.service";
import { CommonTableExclusionService } from "../../services/common-table/common-table-exclusion.service";
import { CommonTableFilterService } from "../../services/common-table/common-table-filter.service";
import { CommonTableSortService, SortComputationResult } from "../../services/common-table/common-table-sort.service";
import { CommonTableStateService } from "../../services/common-table/common-table-state.service";
import { CommonTableGridHandlers } from "./components/common-table-grid/common-table-grid.component";
import { CommonTableGridComponent } from "./components/common-table-grid/common-table-grid.component";
import { CommonTableToolbarComponent } from "./components/common-table-toolbar/common-table-toolbar.component";
import { CommonTableFooterComponent } from "./components/common-table-footer/common-table-footer.component";
@Component({
  standalone: true,
  imports: [CommonModule, CommonTableToolbarComponent, CommonTableGridComponent, CommonTableFooterComponent],
  selector: "app-common-table",
  templateUrl: "./common-table.component.html",
  styleUrls: ["./common-table.component.scss"],
})
export class CommonTableComponent<T extends { [key: string]: any }> implements OnChanges, OnInit, OnDestroy {
  private log: LoggerService = inject(LoggerService);
  private clipboard: Clipboard = inject(Clipboard);
  private looker: LookerStudioService = inject(LookerStudioService);
  private facade: CommonTableFacadeService = inject(CommonTableFacadeService);
  private downloadService: CommonTableDownloadService = inject(CommonTableDownloadService);
  private exclusionService: CommonTableExclusionService = inject(CommonTableExclusionService);
  private filterService: CommonTableFilterService = inject(CommonTableFilterService);
  private sortService: CommonTableSortService = inject(CommonTableSortService);
  private stateService: CommonTableStateService = inject(CommonTableStateService);
  private destroyRef: DestroyRef = inject(DestroyRef);

  // table data input bound
  @Input() tableName: string = "";

  @Input() loading: boolean = false;
  @Input() currency!: Currency;
  @Input() editable: boolean = true;

  // NOTE: the input columns do not contain info about whether
  // the column is visible or not
  // this is managed inside the component and localstorage
  @Input() columns!: TableColumn[];
  @Input() rows!: TableRow[];
  @Input() customGrandTotal!: TableRow;

  @Input() showColumnSelectAccordion: boolean = true;
  @Input() showFilterAccordion: boolean = true;
  @Input() lowerCaseInput: boolean = true;

  // this should be passed in from the query parameters by the parent page ideally
  @Input() inputFilters: FilterItem[] = [];
  @Input() selectableRows: boolean = false;

  @Input() showPagination: boolean = true;
  @Input() dynamicPagination: boolean = false;
  @Input() exactRows: number | undefined = undefined;
  @Input() pageSize: number = DEFAULT_PAGE_SIZE;
  @Input() showSelectAllCheckbox: boolean = false;

  @Input() disableDownload: boolean = false;
  @Input() sortWithApi: boolean = false;
  @Input() downloadFileNamePrefix: string = "";
  @Input() downloadSerializer: (row: TableRow, keys: string[]) => string | null = defaultSerializer;
  @Input() downloadKeys: string[] = [];
  @Input() downloadRequestBody?: GroupByPayload;
  @Input() activePage?: number = undefined;
  @Input() totalRowsPayload?: TotalRowsPayload;
  @Input() middleActions?: TemplateRef<unknown>;

  // table event handler
  @Output() changeEvent: EventEmitter<ChangeEvent<T>> = new EventEmitter();
  @Output() dataFiltered: EventEmitter<TableRow[]> = new EventEmitter();
  @Output() dataSorted: EventEmitter<{ data: TableRow[]; newOrder: SortOrder; currentOrder: SortOrder }> =
    new EventEmitter();
  @Output() pageChanged: EventEmitter<PaginationState<TableRow>> = new EventEmitter();
  @Output() filtersChanged: EventEmitter<FilterItem[]> = new EventEmitter();
  @Output() selectedColumns: BehaviorSubject<Partial<TableColumn>[]> = new BehaviorSubject(<Partial<TableColumn>[]>[]);
  @Output() selectAllRowsEvent: EventEmitter<TableRow[]> = new EventEmitter();
  @Output() sortByColumn: EventEmitter<{ column: string[]; order: SortOrder }> = new EventEmitter();
  @Output() deleteRowEvent: EventEmitter<{ action: string; row: TableRow }> = new EventEmitter();
  @Output() updateRowEvent: EventEmitter<{ action: string; row: TableRow | boolean }> = new EventEmitter();

  private lastPaginationEvent: string = "";
  public readonly weekDays: undefined[] = new Array(7);
  public readonly emptyValues: string[] = EMPTY_VALS;
  private readonly destroy$: Subject<void> = new Subject<void>();
  private readonly timeouts: ReturnType<typeof setTimeout>[] = [];

  // class properties
  // // table
  public tableRows: TableRow[] = [];

  // // sorting
  public sortOrders: Record<string, SortOrder> = {};

  // // pagination
  public currentPage: TableRow[] = [];
  public pageNumber: number = 0;

  public readonly backgroundColors: TableBackgroundColors = {
    warning: "rgb(255, 255, 0, 0.2)",
    success: "rgb(144, 238, 144, 0.2)",
    danger: "rgb(250, 0, 0, 0.2)",
    white: "rgb(255, 255, 255, 0.2)",
    default: "auto",
  };

  public readonly daysOfWeek: string[] = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  public findColumnValueTotal: boolean = false;
  public computeMomTrend: boolean = false;
  public deletedColumnNames: string[] = [];
  public editModeRows: number[] = [];
  protected areAllRowsSelected: boolean = false;
  private originalRowValue: TableRow | null = null;
  private editingRowIndex: number | null = null;
  public readonly gridHandlers: CommonTableGridHandlers = {
    buildElementId: (section: string, rowIndex?: number, columnIndex?: number, itemIndex?: number) =>
      this.buildElementId(section, rowIndex, columnIndex, itemIndex),
    trackByColumn: (index: number, column: TableColumn) => this.trackByColumn(index, column),
    handleColumnTooltipClick: (tooltip?: string) => this.handleColumnTooltipClick(tooltip),
    extractUrl: (tooltip?: string) => getTooltipUrl(tooltip),
    sortDataByColumn: (column: TableColumn, columnIndex: number) => this.sortDataByColumn(column, columnIndex),
    selectAllRows: (event: Event, rows: TableRow[]) => this.selectAllRows(event, rows),
    isRowInEditMode: (rowIndex: number) => this.isRowInEditMode(rowIndex),
    notifyCellChange: (
      event: unknown,
      data: unknown,
      column: string,
      row: TableRow,
      options?: Record<string, unknown> | null,
      action?: string,
    ) => this.notifyCellChange(event, data as T, column, row, options ?? null, action),
    copyValue: (value: string) => this.copyValue(value),
    getStateClass: (state: string) => toStateClass(state),
    getButtonConfig: (state: string) => getActionButtonConfig(state),
    toggleEditMode: (rowIndex: number) => this.toggleEditMode(rowIndex),
    deleteRow: (row: TableRow) => this.deleteRow(row),
    saveRowChanges: (row: TableRow, rowIndex: number) => this.saveRowChanges(row, rowIndex),
    cancelRowEdit: (rowIndex: number) => this.cancelRowEdit(rowIndex),
    handleExclusionAdd: (
      resources: Array<{ exclude?: Array<{ key: string; value: string }> }>,
      type: "start" | "stop",
    ) => this.handleExclusionAdd(resources as Resource[], type),
    columnNotExcluded: (column: TableColumn | TableCell<unknown>, index: number) =>
      this.columnNotExcluded(column as TableColumn | TableCell<T>, index),
    columnTotal: (index: number) => this.columnTotal(index),
    columnMomTrend: (index: number) => this.columnMomTrend(index),
    isGroupSchedulerTable: () => this.isGroupSchedulerTable(),
  };
  public readonly exclusionTypes: ExclusionTypeConfig[] = [
    {
      title: "Stop Exclusions",
      type: "stop",
      index: 0,
      buttonText: "New Stop",
      tooltip: "Add new Stop exclusion",
      colorClass: "text-danger",
      btnClass: "btn-danger",
      headerClass: "stop-header",
      getFn: (data: Resource[]) => this.exclusionService.getStopExclusions(data),
    },
    {
      title: "Start Exclusions",
      type: "start",
      index: 1,
      buttonText: "New Start",
      tooltip: "Add new Start exclusion",
      colorClass: "text-success mt-3",
      btnClass: "btn-success",
      headerClass: "start-header",
      getFn: (data: Resource[]) => this.exclusionService.getStartExclusions(data),
    },
  ];

  ngOnInit(): void {
    const state: TableState = this.getTableState();

    this.log.info("found state in localstorage:", state);

    this.pageSize = state.pageSize ?? this.pageSize ?? DEFAULT_PAGE_SIZE;
    this.pageNumber = state.pageNumber ?? this.pageNumber ?? 0;

    this.publishPaginationEvent({
      pageSize: state.pageSize ?? this.pageSize ?? DEFAULT_PAGE_SIZE,
      pageNumber: state.pageNumber ?? this.pageNumber ?? 0,
    });

    if (this.inputFilters?.length === 0) {
      this.inputFilters = state?.filterItems ?? [];
      this.filtersChanged.next(state?.filterItems ?? []);
    }

    if (((state.selectedColumns || []).length ?? 0) > 0) {
      for (const { selected, title } of state.selectedColumns) {
        const column: TableColumn | undefined = this.columns.find((c) => c?.title === title);
        if (column && selected !== undefined) {
          column.selected = selected;
          if (!selected) {
            this.deletedColumnNames.push(column.title);
          }
        }
      }
      this.selectedColumns.next(this.columns);
    }

    this.facade.initializeMultiSelectControls(this.currentPage, this.columns, this.destroy$, this.onMultiSelectChange);
  }

  private readonly onMultiSelectChange = (event: unknown, data: unknown, column: string, row: TableRow): void => {
    this.notifyCellChange(event, data as T, column, row);
  };

  public toggleEditMode(rowIndex: number): void {
    this.applyEditModeState(
      this.facade.toggleEditMode({
        rowIndex,
        currentPage: this.currentPage,
        columns: this.columns,
        editModeRows: this.editModeRows,
        originalRowValue: this.originalRowValue,
        editingRowIndex: this.editingRowIndex,
        destroy$: this.destroy$,
        onCellChange: this.onMultiSelectChange,
      }),
    );
  }

  private applyEditModeState(editState: EditModeState): void {
    this.editModeRows = editState.editModeRows;
    this.originalRowValue = editState.originalRowValue;
    this.editingRowIndex = editState.editingRowIndex;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.log.info("common table changes", changes);
    if (changes.columns) {
      const state: TableState = this.getTableState();
      let selectedColumns: Partial<TableColumn>[] = state.selectedColumns || [];
      if (selectedColumns.length === 0) {
        selectedColumns = changes.columns.currentValue;
      }
      selectedColumns = selectedColumns.map(({ selected, ...c }) => ({
        ...c,
        selected: selected === undefined ? true : selected,
      }));
      for (const { title, selected } of selectedColumns) {
        const column: TableColumn | undefined = this.columns.find((c) => c.title === title);
        if (column) {
          column.selected = selected;
        }
      }
      for (const column of this.columns) {
        if (column.sum) this.findColumnValueTotal = true;
        if (column.computeMomTrend) this.computeMomTrend = true;
      }
    }

    if (changes?.inputFilters || changes?.rows) {
      this.log.info("detected changes to data or filters");
      if ((changes?.rows?.currentValue?.length ?? 0) < (changes?.rows?.previousValue?.length ?? 0)) {
        this.pageNumber = 0;
        this.setTableStates({ pageNumber: this.pageNumber });
      }
      this.tableRows = this.getFilteredData(this.rows, this.columns, this.inputFilters);
      this.areAllRowsSelected = this.facade.areAllRowsSelected(this.tableRows);
      if (changes?.rows && !changes?.rows?.firstChange) {
        this.currentPage = this.facade.getCurrentPage(this.tableRows, this.pageNumber, this.pageSize);
      }

      if (changes.inputFilters && !changes.inputFilters.firstChange) {
        this.setTableStates({ filterItems: this.inputFilters });
      }

      if (changes?.rows && this.editModeRows.length > 0) {
        this.log.info("Re-initializing controls for edit mode rows after row changes");
        this.scheduleEditModeControlsRefresh();
      }
    }

    if (this.activePage) {
      this.setTableStates({ pageNumber: this.activePage });
      this.pageNumber = this.activePage;
    }
  }

  // Deferred so the controls are rebuilt after the new rows have been rendered.
  private scheduleEditModeControlsRefresh(): void {
    const timeoutId: ReturnType<typeof setTimeout> = setTimeout(() => {
      for (const rowIndex of this.editModeRows) {
        const row: TableRow | undefined = this.currentPage[rowIndex];
        if (!row) {
          continue;
        }
        this.facade.initializeRowMultiSelectControls(row, this.columns, this.destroy$, this.onMultiSelectChange, false);
      }
    }, 0);
    this.timeouts.push(timeoutId);
  }

  private publishPaginationEvent(event: PaginationState<TableRow>): void {
    if (this.lastPaginationEvent !== "" && this.lastPaginationEvent === JSON.stringify(event)) {
      return;
    }
    this.lastPaginationEvent = JSON.stringify(event);
    this.pageChanged.next(event);
  }

  private get isDownloadAsync(): boolean {
    return this.downloadRequestBody !== undefined && this.downloadRequestBody !== null;
  }
  public uncheckSelectAll(): void {
    if (this.selectAllCb?.nativeElement) {
      this.selectAllCb.nativeElement.checked = false;
      this.areAllRowsSelected = false;
    }
  }

  public get selectAllCb(): { nativeElement: HTMLInputElement } | undefined {
    const selectAllElement = document.getElementById(this.buildElementId("select-all")) as HTMLInputElement | null;
    if (!selectAllElement) {
      return undefined;
    }
    return { nativeElement: selectAllElement };
  }

  public buildElementId(section: string, rowIndex?: number, columnIndex?: number, itemIndex?: number): string {
    const sanitizedTableName: string = (this.tableName || "common-table").replace(/[^a-zA-Z0-9-_]/g, "-");
    const parts: string[] = ["common-table", sanitizedTableName, section];
    if (rowIndex !== undefined) {
      parts.push(`r${rowIndex}`);
    }
    if (columnIndex !== undefined) {
      parts.push(`c${columnIndex}`);
    }
    if (itemIndex !== undefined) {
      parts.push(`i${itemIndex}`);
    }
    return parts.join("-");
  }

  public handleColumnTooltipClick(tooltip?: string): void {
    this.log.debug("handling click on tooltip:", tooltip);
    openTooltipUrl(tooltip);
  }

  public trackByColumn(index: number, column: TableColumn): string {
    return column.label || column.title;
  }

  public notifyCellChange(
    event: unknown,
    data: T,
    column: string,
    row: TableRow,
    options: Record<string, unknown> | null = null,
    action?: string,
  ): void {
    const columnIdx: number = this.columns.findIndex((c) => c.title === column);
    const cell: TableCell<unknown> | undefined = row[columnIdx];
    if (!cell) {
      return;
    }

    this.log.info({ msg: "Cell change detected", column, newValue: cell.control ? cell.control.value : cell.value });

    const result = this.facade.processCellChange<T>(
      event,
      data,
      row,
      cell,
      action,
      this.isRowInEditMode(this.currentPage.indexOf(row)),
      this.tableRows,
    );

    if (result.shouldReturnEarly) {
      return;
    }

    this.areAllRowsSelected = result.areAllRowsSelected;
    this.changeEvent.next(
      this.facade.createCellChangePayload<T>(event, result.processedData, column, options, row, action),
    );
  }

  // customize table columns handler
  public handleColumnSelection(updatedColumns: Partial<TableColumn>[]): void {
    this.log.info("handling column change:", updatedColumns);
    this.deletedColumnNames = this.facade.applyColumnSelection(this.columns, updatedColumns);
    this.setTableStates({ selectedColumns: this.columns });
    this.selectedColumns.next(this.columns);
  }

  public handlePaginationChange(state: PaginationState<TableRow>): void {
    // Check if anything ACTUALLY changed to avoid redundant processing
    if (
      state.pageNumber === this.pageNumber &&
      state.pageSize === this.pageSize &&
      this.currentPage.length === state.currentPage?.length
    ) {
      return;
    }
    this.log.info("handling pagination change:", state);
    const { pageNumber, pageSize, currentPage } = state;
    this.pageSize = pageSize;
    this.pageNumber = pageNumber;
    this.currentPage = currentPage as TableRow[];
    this.areAllRowsSelected = this.facade.areAllRowsSelected(this.tableRows);
    this.timeouts.push(setTimeout(() => this.setTableStates({ pageNumber, pageSize })));
    this.publishPaginationEvent(state);
  }

  public handleExclusionAdd(resources: Resource[], type: "start" | "stop"): void {
    this.exclusionService.addExclusion(resources, type);
  }

  public sortDataByColumn(column: TableColumn, columnIdx: number): void {
    this.log.info("sort on ", column, columnIdx);

    const result: SortComputationResult = this.sortService.computeSortResult(
      column?.title,
      this.sortOrders,
      this.sortWithApi,
      this.tableRows,
      columnIdx,
      this.customGrandTotal,
    );

    this.sortOrders[column.title] = result.newOrder;

    if (this.sortWithApi) {
      this.sortByColumn.emit({ column: [column.title], order: result.newOrder });
      return;
    }

    this.tableRows = result.sortedRows ?? this.tableRows;
    this.currentPage = this.facade.getCurrentPage(this.tableRows, this.pageNumber, this.pageSize);
    this.dataSorted.next({
      currentOrder: result.currentOrder,
      newOrder: result.newOrder,
      data: this.tableRows,
    });
  }

  private getFilteredData(rows: TableRow[], columns: TableColumn[], filterItems: FilterItem[]): TableRow[] {
    this.log.info("filtering data with:", filterItems);
    const foundData: TableRow[] = this.filterService.filterRows(rows, columns, filterItems);
    this.log.info("filtered data", foundData);
    this.dataFiltered.next(foundData);
    this.pageNumber = 0;
    this.setTableStates({ pageNumber: this.pageNumber });
    return foundData;
  }

  public getTableState(): TableState {
    return this.facade.getTableState(this.tableName ?? "");
  }

  public setTableStates(updates: Partial<TableState>): void {
    if (this.pageSize > 0) {
      this.pageNumber = this.stateService.normalizePageNumber(
        this.pageNumber ?? 0,
        this.pageSize,
        this.rows?.length ?? 0,
      );
    }
    this.facade.setTableState(this.tableName ?? "", this.pageSize, this.selectedColumns?.value ?? [], updates);
  }

  public handleFiltersChange(filterItems: FilterItem[]): void {
    this.log.info("new filters", filterItems);
    this.inputFilters = filterItems;
    this.tableRows = this.getFilteredData(this.rows, this.columns, this.inputFilters);
    this.currentPage = this.facade.getCurrentPage(this.tableRows, this.pageNumber, this.pageSize);
    this.filtersChanged.next(filterItems);
  }

  public columnTotal(index: number): string {
    return getColumnTotal(
      this.tableRows,
      index,
      this.currency,
      (value: number) => this.looker.formatting.formatNumber(value, 2).fmt,
    );
  }

  public columnMomTrend(index: number): string {
    return getColumnMomTrend(this.tableRows, index);
  }

  public async download(type: DownloadType): Promise<void> {
    const useAsyncDownload: boolean = this.downloadService.shouldUseAsyncDownload(
      this.tableName,
      type,
      environment?.featureToggles?.asyncExport ?? false,
      this.isDownloadAsync,
    );

    if (useAsyncDownload) {
      this.downloadAsync();
      return;
    }

    await this.downloadSync(type);
  }

  public downloadAsync(): void {
    this.downloadService.startAsyncDownload(
      this.downloadRequestBody as GroupByPayload,
      this.currency,
      this.columns,
      this.destroyRef,
    );
  }

  public async downloadSync(type: DownloadType): Promise<void> {
    await this.downloadService.downloadSync({
      tableName: this.tableName,
      downloadFileNamePrefix: this.downloadFileNamePrefix,
      type,
      columns: this.columns,
      rows: this.rows,
      tableRows: this.tableRows,
      selectableRows: this.selectableRows,
      downloadKeys: this.downloadKeys,
      downloadSerializer: this.downloadSerializer,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
  }

  public copyValue(value: string): void {
    if (!value) {
      return;
    }
    const normalizedValue: string = value.startsWith("mailto:") ? value.replace("mailto:", "") : value;
    this.clipboard.copy(normalizedValue);
  }

  public columnNotExcluded(column: TableColumn | TableCell<T>, index: number): boolean {
    if (this.deletedColumnNames.length === 0) {
      return true;
    }
    if (!column.title) {
      column.title = this.columns[index].title;
    }
    return !this.deletedColumnNames.includes(column.title);
  }

  public selectAllRows(event: Event, rows: TableRow[]): void {
    this.log.info("selecting all rows", event, rows);
    const { areAllRowsSelected, rowsToEmit } = this.facade.toggleSelectAllRows(rows);
    this.areAllRowsSelected = areAllRowsSelected;
    this.selectAllRowsEvent.next(rowsToEmit);
  }

  public deleteRow(row: TableRow): void {
    this.updateRowEvent.emit({ action: "delete", row });
  }

  public saveRowChanges(row: TableRow, rowIndex: number): void {
    this.originalRowValue = null;
    this.editingRowIndex = null;
    this.toggleEditMode(rowIndex);
    this.updateRowEvent.emit({ action: "update", row });
  }

  public isRowInEditMode(rowIndex: number): boolean {
    return this.editModeRows.includes(rowIndex);
  }

  public cancelRowEdit(rowIndex: number): void {
    const { currentPage, editState } = this.facade.cancelRowEdit(
      rowIndex,
      this.currentPage,
      this.columns,
      this.editModeRows,
      this.originalRowValue,
      this.editingRowIndex,
      this.destroy$,
      this.onMultiSelectChange,
    );
    this.currentPage = currentPage;
    this.applyEditModeState(editState);
  }

  public isGroupSchedulerTable(): boolean {
    return this.tableName === "group-scheduler-table";
  }
}
